import { useEffect } from 'react';
import L from 'leaflet';
import { TRACK_PROFILES, WP_KINDS, wpRef, wpLabel, slopeSegs, SLOPE_COLORS, hasAlt } from '../lib/track';
import { fmtAlt } from '../lib/altitude';
import { POI_ICON, POI_COLOR } from '../lib/trackModes';

const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
export const poiHtml = (k) => `<span class="poi-mk" style="--c:${POI_COLOR[k] || '#16a34a'}">${POI_ICON[k] || '📍'}</span>`;
const wpShape = (w) => (POI_ICON[w.poi]
  ? L.marker([w.lat, w.lon], { icon: L.divIcon({ className: `poi-icon wp-poi-${w.poi}`, html: poiHtml(w.poi), iconSize: [30, 30], iconAnchor: [15, 15] }) })
  : L.circleMarker([w.lat, w.lon], { radius: 8, color: '#fff', weight: 2.5, fillColor: WP_KINDS[w.kind]?.color || '#64748b', fillOpacity: 1, className: `wp-mk wp-${w.kind}` }));
const wpMarker = (t, tr, w) => wpShape(w)
  .bindPopup(`<b>${esc(wpRef(tr, w))}</b> ${esc(wpLabel(t, w))}<br><small>${new Date(w.t).toLocaleString()} · ${esc(fmtAlt(w.alt))}</small>${w.note ? `<br>${esc(w.note)}` : ''}${w.photos || w.videos || w.memos ? `<br>📷 ${w.photos || 0} · 🎬 ${w.videos || 0} · 🎙️ ${w.memos || 0}` : ''}`);
import { useTrackRec } from '../lib/trackRec';


// saved + live Follow-Me routes on the main map
export default function useTrackLayer(mapRef, on, tracks, t) {
  const rec = useTrackRec();
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !on) return undefined;
    const g = L.layerGroup().addTo(map);
    (tracks || []).filter((tr) => !tr.hidden).forEach((tr) => {
      const c = TRACK_PROFILES[tr.profile]?.color || '#0ea5e9';
      slopeSegs(tr.pts).forEach(({ ln, k }) => {
        g.addLayer(L.polyline(ln, { color: '#fff', weight: 7, opacity: 0.8, interactive: false }));
        g.addLayer(L.polyline(ln, { color: SLOPE_COLORS[k] || c, weight: 4, interactive: false, className: `trk-line trk-${tr.id} trk-slope-${k || 'none'}` }));
      });
      (tr.wps || []).forEach((w) => g.addLayer(wpMarker(t, tr, w)));
    });
    return () => g.remove();
  }, [mapRef, on, tracks, t]);
  const legendOn = (on && (tracks || []).some((tr) => !tr.hidden && hasAlt(tr.pts))) || hasAlt(rec?.pts);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !legendOn) return undefined;
    const ctl = L.control({ position: 'bottomleft' });
    ctl.onAdd = () => { const d = L.DomUtil.create('div', 'slope-legend'); d.setAttribute('data-testid', 'slope-legend'); d.innerHTML = `<b>${esc(t.slTitle)}</b>${[['g', '< 3 %'], ['y', '3–8 %'], ['r', '> 8 %']].map(([k, l]) => `<span><i style="background:${SLOPE_COLORS[k]}"></i>${l}</span>`).join('')}`; return d; };
    ctl.addTo(map);
    return () => ctl.remove();
  }, [mapRef, legendOn, t]);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !rec?.pts?.length) return undefined;
    const c = TRACK_PROFILES[rec.profile].color, last = rec.pts[rec.pts.length - 1];
    const g = L.layerGroup().addTo(map);
    slopeSegs(rec.pts).forEach(({ ln, k }) => g.addLayer(L.polyline(ln, { color: SLOPE_COLORS[k] || c, weight: 5, dashArray: rec.status === 'pause' ? '6 6' : null, interactive: false, className: `trk-live trk-slope-${k || 'none'}` })));
    g.addLayer(L.circleMarker([last.lat, last.lon], { radius: 7, color: '#fff', weight: 2, fillColor: c, fillOpacity: 1, interactive: false }));
    (rec.wps || []).forEach((w) => g.addLayer(wpMarker(t, rec, w)));
    return () => g.remove();
  }, [mapRef, rec, t]);
}
